import {CreateFormHandler} from "./Form";
import {Page} from "../pages";
import FormattedButton from "../FormattedButton";
import {useAddressBookCRUD} from "./tools";
import {AddressBookCRUD} from "./AddressBookCRUD";
import Loading from "@/components/breadcrumbs/Loading";
import {
    useAddAddressBookEntryMutation,
    usePersonalTargetQuery,
    useUpdateAddressBookEntryMutation,
} from "./queries.generated";
import {defineMessage, useIntl} from "react-intl";
import {useDebounceState} from "@/hooks";
import {useEffect} from "react";
import {Box} from "@mui/system";
import {TextField} from "@mui/material";
import {commonTranslations} from "@/utils/translations";

export const PersonalAddressBookPage = ({extensionId}: {extensionId: number}) => {
    const {data} = usePersonalTargetQuery({
        variables: {extensionId},
    });

    if (!data) return <Loading />;

    const ext = data.users.items[0];

    return <Wrapper target={`${ext.number.shortNumber}@${ext.number.domain.name}`} />;
};

const Wrapper = ({target}: {target: string}) => {
    const {data, deleteEntryName, formHandler, refetch, setDeleteEntryName, setFormHandler} =
        useAddressBookCRUD(target);

    const [filter, filterDebounced, setFilter] = useDebounceState("", 300);
    const {formatMessage} = useIntl();

    const [createMutation] = useAddAddressBookEntryMutation();
    const [updateMutation] = useUpdateAddressBookEntryMutation();

    const defaultFormHandler = new CreateFormHandler(createMutation);

    useEffect(() => {
        refetch({target, filterValue: filterDebounced});
    }, [filterDebounced, refetch, target]);

    return (
        <Page
            title={defineMessage({id: "address_book.title", defaultMessage: "Address book"})}
            controls={
                <Box sx={{display: "flex", alignItems: "baseline"}}>
                    <TextField
                        variant="standard"
                        value={filter}
                        placeholder={formatMessage(commonTranslations.filterPlaceholders.nameOrPhoneNumber)}
                        onChange={(event) => setFilter(event.target.value)}
                        sx={{flexGrow: 1}}
                    />
                    <Box sx={{ml: 1}}>
                        <FormattedButton
                            onClick={() => setFormHandler(defaultFormHandler)}
                            message={defineMessage({id: "address_book.create", defaultMessage: "Create new entry"})}
                        />
                    </Box>
                </Box>
            }
        >
            <AddressBookCRUD
                target={target}
                data={data}
                deleteEntryName={deleteEntryName}
                formHandler={formHandler}
                refetch={refetch}
                setDeleteEntryName={setDeleteEntryName}
                setFormHandler={setFormHandler}
                updateMutation={updateMutation}
            />
        </Page>
    );
};
